import { SizerPointPosition } from '@/modules/map-editor';
import type { PointerPosition } from '@/modules/map-editor';
import { Scale } from '@/services/canvas/Scale';

export function getPointerPosition(
  event: MouseEvent | TouchEvent,
  canvas: HTMLCanvasElement
): PointerPosition {
  const pixelRatio = window.devicePixelRatio;
  const canvasBounds = canvas.getBoundingClientRect();

  const sourceEvent = (event as TouchEvent).targetTouches
    ? (event as TouchEvent).targetTouches[0]
    : (event as MouseEvent);

  const clientX = sourceEvent.clientX * pixelRatio;
  const clientY = sourceEvent.clientY * pixelRatio;

  return {
    x: clientX - canvasBounds.left * pixelRatio,
    y: clientY - canvasBounds.top * pixelRatio,
    clientX,
    clientY,
    movementX: 0, // Will be calculated separately
    movementY: 0
  };
}

export function calculateMovement(
  currentPosition: PointerPosition,
  previousPosition: PointerPosition
): { movementX: number; movementY: number } {
  return {
    movementX: currentPosition.x - previousPosition.x,
    movementY: currentPosition.y - previousPosition.y
  };
}

export function getSizerCursor(position: SizerPointPosition): string {
  const cursors: Record<SizerPointPosition, string> = {
    [SizerPointPosition.TOP_LEFT]: 'nwse-resize',
    [SizerPointPosition.TOP_RIGHT]: 'nesw-resize',
    [SizerPointPosition.BOTTOM_LEFT]: 'nesw-resize',
    [SizerPointPosition.BOTTOM_RIGHT]: 'nwse-resize'
  };
  return cursors[position];
}

/**
 * Determine if pointer is within the map area boundaries.
 *
 * @param ctx - Canvas 2D context (used for canvas center calculation)
 * @param mouseX - Pointer X position in canvas coordinates
 * @param mouseY - Pointer Y position in canvas coordinates
 * @param area - Map area rectangle (x, y, width, height)
 * @param zoomLevel - Current zoom level
 * @returns true if pointer is inside the map area
 */
export function isPointerInCanvasArea(
  ctx: CanvasRenderingContext2D,
  mouseX: number,
  mouseY: number,
  area: { x: number; y: number; width: number; height: number },
  zoomLevel: number
): boolean {
  const scaledX = Scale.x(area.x, ctx.canvas.width / 2, zoomLevel);
  const scaledY = Scale.y(area.y, ctx.canvas.height / 2, zoomLevel);
  const scaledWidth = area.width * zoomLevel;
  const scaledHeight = area.height * zoomLevel;

  return (
    mouseX >= scaledX &&
    mouseX <= scaledX + scaledWidth &&
    mouseY >= scaledY &&
    mouseY <= scaledY + scaledHeight
  );
}

export function getTopLevelElementId(elements: any[]): number {
  const suitableElements = elements.filter(
    element => element.hover && !element.locked
  );

  if (!suitableElements.length) return -1;

  const indexes = suitableElements.map(element => element.id);
  return indexes[suitableElements.length - 1];
}
